import { createClient, SupabaseClient } from '@supabase/supabase-js';

// Module-level client
let _supabase: SupabaseClient | null = null;
let _lockCallCount = 0; // Track lock calls to skip only initialization
let _sessionReady: Promise<void> | null = null; // Track when session is ready
let _sessionReadyResolver: (() => void) | null = null; // To resolve the promise externally

// Timeout constants
const SESSION_RECOVERY_TIMEOUT = 8000; // 8 seconds max for session recovery
const AUTH_OPERATION_TIMEOUT = 5000; // 5 seconds for individual auth operations

/**
 * Helper to wrap a promise with a timeout
 */
function withAuthTimeout<T>(promise: Promise<T>, timeoutMs: number, operationName: string): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout>;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error(`[Supabase] ${operationName} timed out after ${timeoutMs}ms`));
    }, timeoutMs);
  });

  return Promise.race([promise, timeoutPromise]).finally(() => {
    clearTimeout(timeoutId);
  });
}

// CRITICAL: Override navigator.locks to prevent Supabase from using it
// This completely bypasses the locking mechanism that causes deadlocks
if (typeof window !== 'undefined' && navigator.locks) {
  const originalRequest = navigator.locks.request.bind(navigator.locks);

  // Override navigator.locks.request to skip locking for Supabase auth
  (navigator.locks as any).request = async (
    name: string,
    optionsOrCallback: any,
    maybeCallback?: any
  ) => {
    // Check if this is a Supabase auth lock (various patterns)
    if (name.includes('sb-') || name.includes('supabase') || name.includes('auth')) {
      console.log('[Supabase] Bypassing navigator.locks for:', name);
      // Execute the callback directly without acquiring a lock
      const callback = typeof optionsOrCallback === 'function' ? optionsOrCallback : maybeCallback;
      if (callback) {
        return callback({ name, mode: 'exclusive' });
      }
      return;
    }
    // For non-Supabase locks, use the original implementation
    return originalRequest(name, optionsOrCallback, maybeCallback);
  };

  console.log('[Supabase] navigator.locks.request patched to bypass auth locks');
}

// CRITICAL: Override BroadcastChannel to prevent cross-tab sync issues
// Supabase uses BroadcastChannel for cross-tab auth sync, which can hang
// if there are stale listeners from HMR or crashed tabs
if (typeof window !== 'undefined' && typeof BroadcastChannel !== 'undefined') {
  const OriginalBroadcastChannel = BroadcastChannel;

  (window as any).BroadcastChannel = class MockBroadcastChannel {
    name: string;
    onmessage: ((event: MessageEvent) => void) | null = null;
    onmessageerror: ((event: MessageEvent) => void) | null = null;

    constructor(name: string) {
      this.name = name;
      // Only log for Supabase channels
      if (name.includes('sb-') || name.includes('supabase') || name.includes('auth')) {
        console.log('[BroadcastChannel] Mocking channel:', name);
      } else {
        // For non-Supabase channels, use the real implementation
        return new OriginalBroadcastChannel(name) as any;
      }
    }

    postMessage(_message: any) {
      // Silently ignore - no cross-tab sync for Supabase
    }

    close() {
      // No-op
    }

    addEventListener(_type: string, _listener: any) {
      // No-op
    }

    removeEventListener(_type: string, _listener: any) {
      // No-op
    }

    dispatchEvent(_event: Event): boolean {
      return true;
    }
  };

  console.log('[Supabase] BroadcastChannel mocked to prevent cross-tab sync hangs');
}

function getSupabaseClient(): SupabaseClient {
  // Return module-level client if already created
  if (_supabase) {
    return _supabase;
  }

  // Check window cache, but only if it's a valid client (not from a broken HMR state)
  if (typeof window !== 'undefined' && (window as any).__supabaseClient) {
    const cached = (window as any).__supabaseClient;
    // Verify it's still functional by checking if auth exists
    if (cached && cached.auth) {
      _supabase = cached;
      return cached;
    }
  }

  console.log('[Supabase] Creating new client instance');

  // Create new client - completely disable navigator.locks
  // This prevents deadlocks from stale locks that can occur during HMR or tab crashes
  _supabase = createClient(
    import.meta.env.VITE_SUPABASE_URL,
    import.meta.env.VITE_SUPABASE_ANON_KEY,
    {
      auth: {
        storage: typeof window !== 'undefined' ? window.localStorage : undefined,
        storageKey: 'sb-urkfvgqpzeuixgdayrex-auth-token',
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        flowType: 'implicit',
        // Custom lock function - COMPLETELY bypass locking mechanism
        // Execute fn() directly without any lock acquisition
        // This prevents deadlocks from navigator.locks or BroadcastChannel issues
        lock: async (name: string, acquireTimeout: number, fn: () => Promise<any>) => {
          _lockCallCount++;
          const callNum = _lockCallCount;

          if (callNum <= 3) {
            console.log(`[Supabase.lock] Call #${callNum} bypassing lock for: ${name}`);
          }

          try {
            // Just execute the function directly without any locking or timeout
            // If it hangs, the browser will handle it (not our problem)
            const result = await fn();

            if (callNum <= 3) {
              console.log(`[Supabase.lock] Call #${callNum} completed`);
            }

            return result;
          } catch (e: any) {
            console.error(`[Supabase.lock] Call #${callNum} failed for ${name}:`, e);
            // Don't throw - return undefined to allow app to continue
            return undefined;
          }
        },
      },
    }
  );

  // Store on window to survive HMR
  if (typeof window !== 'undefined') {
    (window as any).__supabaseClient = _supabase;
    (window as any).supabase = _supabase;
    (window as any).supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    (window as any).supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    // Initialize session recovery on client creation
    // With increased timeout (10s), this should complete without hanging
    createSessionReadyPromise();
  }

  return _supabase;
}

/**
 * Create or recreate the session ready promise
 * This should be called on client creation and on auth state changes
 */
function createSessionReadyPromise(): void {
  // If there's already a promise that hasn't resolved, don't create a new one
  if (_sessionReady && _sessionReadyResolver) {
    return;
  }

  console.log('[Supabase] Creating session ready promise...');

  _sessionReady = new Promise<void>((resolve) => {
    _sessionReadyResolver = resolve;

    // Wrap the entire recovery in a timeout
    const recoveryTimeout = setTimeout(() => {
      console.warn('[Supabase] Session recovery timed out after', SESSION_RECOVERY_TIMEOUT, 'ms');
      _sessionReadyResolver = null;
      resolve(); // Resolve anyway so app doesn't hang
    }, SESSION_RECOVERY_TIMEOUT);

    // Do the actual recovery
    (async () => {
      const storageKey = 'sb-urkfvgqpzeuixgdayrex-auth-token';
      const storedSession = localStorage.getItem(storageKey);

      if (!storedSession) {
        console.log('[Supabase] No stored session found');
        clearTimeout(recoveryTimeout);
        _sessionReadyResolver = null;
        resolve();
        return;
      }

      console.log('[Supabase] Found stored session, restoring...');

      try {
        const parsed = JSON.parse(storedSession);

        // Check if session is expired
        const expiresAt = parsed.expires_at;
        const now = Math.floor(Date.now() / 1000);

        if (expiresAt && expiresAt < now) {
          console.log('[Supabase] Stored session expired, will refresh...');
          try {
            // Add timeout to getSession call
            const { data } = await withAuthTimeout(
              _supabase!.auth.getSession(),
              AUTH_OPERATION_TIMEOUT,
              'getSession (refresh expired)'
            );
            if (data.session) {
              console.log('[Supabase] Session refreshed:', data.session.user?.email);
            } else {
              console.log('[Supabase] Session refresh failed, user needs to re-login');
            }
          } catch (e) {
            console.warn('[Supabase] getSession timed out during refresh:', e);
          }
          clearTimeout(recoveryTimeout);
          _sessionReadyResolver = null;
          resolve();
          return;
        }

        // Session not expired - proactively set it to ensure it's loaded
        if (parsed.access_token && parsed.refresh_token) {
          console.log('[Supabase] Proactively setting session from localStorage');
          try {
            const { data, error } = await withAuthTimeout(
              _supabase!.auth.setSession({
                access_token: parsed.access_token,
                refresh_token: parsed.refresh_token
              }),
              AUTH_OPERATION_TIMEOUT,
              'setSession'
            );

            if (error) {
              console.error('[Supabase] Error setting session:', error);
              // Try getSession as fallback with timeout
              try {
                const { data: sessionData } = await withAuthTimeout(
                  _supabase!.auth.getSession(),
                  AUTH_OPERATION_TIMEOUT,
                  'getSession (fallback)'
                );
                if (sessionData.session) {
                  console.log('[Supabase] Fallback getSession worked:', sessionData.session.user?.email);
                }
              } catch (e) {
                console.warn('[Supabase] Fallback getSession timed out:', e);
              }
            } else if (data.session) {
              console.log('[Supabase] Session restored:', data.session.user?.email);
            }
          } catch (e) {
            console.warn('[Supabase] setSession timed out (non-fatal, continuing):', e);
            // Don't throw - app can work offline and recover later
          }
        }
      } catch (e) {
        console.error('[Supabase] Exception restoring session:', e);
      }
      clearTimeout(recoveryTimeout);
      _sessionReadyResolver = null;
      resolve();
    })();
  });
}

/**
 * Reset the session ready promise - call this on auth state changes
 */
export function resetSessionReady(): void {
  console.log('[Supabase] Resetting session ready state...');
  // Resolve any pending promise first
  if (_sessionReadyResolver) {
    _sessionReadyResolver();
  }
  _sessionReady = null;
  _sessionReadyResolver = null;
  // Recreate the promise for the new session
  if (_supabase) {
    createSessionReadyPromise();
  }
}

// Export a proxy that lazily gets the client
export const supabase = new Proxy({} as SupabaseClient, {
  get(_, prop) {
    const client = getSupabaseClient();
    const value = (client as any)[prop];
    if (typeof value === 'function') {
      return value.bind(client);
    }
    return value;
  },
});

/**
 * Wait for the Supabase session to be recovered from localStorage.
 * Call this before any auth operations that depend on the session being ready.
 * Has a fallback timeout to prevent indefinite hanging.
 */
export async function waitForSessionReady(): Promise<void> {
  // Ensure client is created first (which initializes session recovery)
  getSupabaseClient();

  // Wait for session recovery to complete with a fallback timeout
  if (_sessionReady) {
    const fallbackTimeout = new Promise<void>((resolve) => {
      setTimeout(() => {
        console.warn('[Supabase] waitForSessionReady fallback timeout triggered');
        resolve();
      }, SESSION_RECOVERY_TIMEOUT + 2000); // Give a bit more than the internal timeout
    });

    await Promise.race([_sessionReady, fallbackTimeout]);
  }
}

// Debug helpers - expose on window for console testing
if (typeof window !== 'undefined') {
  (window as any).testSupabaseAuth = async () => {
    console.log('=== Testing Supabase Auth ===');
    const client = getSupabaseClient();

    // Test 1: getSession (should be synchronous from localStorage)
    console.log('Test 1: getSession...');
    const start1 = Date.now();
    try {
      const { data, error } = await client.auth.getSession();
      console.log(`getSession completed in ${Date.now() - start1}ms`);
      if (error) console.error('getSession error:', error);
      else console.log('Session:', data.session ? 'exists' : 'null');
    } catch (e) {
      console.error('getSession threw:', e);
    }

    // Test 2: getUser (makes API call)
    console.log('Test 2: getUser...');
    const start2 = Date.now();
    try {
      const { data, error } = await client.auth.getUser();
      console.log(`getUser completed in ${Date.now() - start2}ms`);
      if (error) console.error('getUser error:', error);
      else console.log('User:', data.user?.email || 'null');
    } catch (e) {
      console.error('getUser threw:', e);
    }

    // Test 3: Direct API call (bypassing Supabase client entirely)
    console.log('Test 3: Direct API call...');
    const start3 = Date.now();
    try {
      const storageKey = 'sb-urkfvgqpzeuixgdayrex-auth-token';
      const stored = localStorage.getItem(storageKey);
      if (!stored) {
        console.log('No session in localStorage');
        return;
      }
      const session = JSON.parse(stored);
      const token = session.access_token;

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/auth/v1/user`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        }
      });
      console.log(`Direct API completed in ${Date.now() - start3}ms, status: ${response.status}`);
      if (response.ok) {
        const user = await response.json();
        console.log('Direct API user:', user.email);
      }
    } catch (e) {
      console.error('Direct API threw:', e);
    }

    console.log('=== Auth Tests Complete ===');
  };

  // Check what's in localStorage for auth
  (window as any).checkAuthStorage = () => {
    const storageKey = 'sb-urkfvgqpzeuixgdayrex-auth-token';
    const stored = localStorage.getItem(storageKey);
    if (!stored) {
      console.log('No auth token in localStorage');
      return null;
    }
    try {
      const parsed = JSON.parse(stored);
      console.log('Auth storage contents:', {
        hasAccessToken: !!parsed.access_token,
        hasRefreshToken: !!parsed.refresh_token,
        hasUser: !!parsed.user,
        userEmail: parsed.user?.email,
        expiresAt: parsed.expires_at ? new Date(parsed.expires_at * 1000).toISOString() : 'unknown',
      });
      return parsed;
    } catch (e) {
      console.error('Failed to parse auth storage:', e);
      return null;
    }
  };
}

/**
 * Auth health status
 */
export type AuthHealthStatus = 'healthy' | 'recovered' | 'needs_login' | 'checking';

// Track auth health state for UI
let _authHealthStatus: AuthHealthStatus = 'checking';
let _authHealthListeners: Set<(status: AuthHealthStatus) => void> = new Set();
let _authRecoveryInProgress = false;
let _lastAuthCheck = 0;
const AUTH_CHECK_INTERVAL = 30000; // Check every 30 seconds minimum

/**
 * Check if auth is healthy by checking localStorage directly
 * This avoids async calls to getSession() which can hang due to locking issues
 */
export async function isAuthHealthy(): Promise<boolean> {
  try {
    const storageKey = 'sb-urkfvgqpzeuixgdayrex-auth-token';
    const stored = localStorage.getItem(storageKey);

    if (!stored) {
      return false; // No session stored
    }

    const parsed = JSON.parse(stored);

    // Check if we have the required tokens
    if (!parsed.access_token || !parsed.refresh_token) {
      return false;
    }

    // Check if session is expired (with 60 second buffer)
    const expiresAt = parsed.expires_at;
    const now = Math.floor(Date.now() / 1000);

    if (expiresAt && expiresAt < now - 60) {
      // Session expired more than a minute ago - need refresh
      // But still return true if we have a refresh token - we can recover
      return !!parsed.refresh_token;
    }

    // Session exists and is not expired
    return true;
  } catch (e) {
    console.error('[Supabase] isAuthHealthy error:', e);
    return false;
  }
}

/**
 * Reset auth state completely - clears stored session and reinitializes client
 * Use this when auth is in a corrupted state (e.g., after HMR issues)
 */
export function resetAuthState(): void {
  console.log('[Supabase] Resetting auth state...');

  // Clear stored session
  const storageKey = 'sb-urkfvgqpzeuixgdayrex-auth-token';
  localStorage.removeItem(storageKey);

  // Clear module-level client
  _supabase = null;
  _sessionReady = null;

  // Clear window cache
  if (typeof window !== 'undefined') {
    delete (window as any).__supabaseClient;
    delete (window as any).supabase;
  }

  // Update status
  updateAuthHealthStatus('needs_login');

  console.log('[Supabase] Auth state reset complete. User will need to sign in again.');
}

/**
 * Force refresh the session token
 * Returns true if refresh succeeded, false otherwise
 */
export async function refreshSession(): Promise<boolean> {
  try {
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Session refresh timeout')), 5000);
    });

    const refreshPromise = supabase.auth.refreshSession();
    const { data, error } = await Promise.race([refreshPromise, timeoutPromise]);

    if (error) {
      console.error('[Supabase] Session refresh failed:', error);
      return false;
    }
    console.log('[Supabase] Session refreshed successfully:', data.session?.user?.email);
    return true;
  } catch (e) {
    console.error('[Supabase] Session refresh exception:', e);
    return false;
  }
}

/**
 * Update auth health status and notify listeners
 */
function updateAuthHealthStatus(status: AuthHealthStatus): void {
  if (_authHealthStatus !== status) {
    console.log(`[Supabase] Auth health status: ${_authHealthStatus} -> ${status}`);
    _authHealthStatus = status;
    _authHealthListeners.forEach(listener => listener(status));
  }
}

/**
 * Subscribe to auth health status changes
 */
export function onAuthHealthChange(callback: (status: AuthHealthStatus) => void): () => void {
  _authHealthListeners.add(callback);
  // Immediately call with current status
  callback(_authHealthStatus);
  return () => _authHealthListeners.delete(callback);
}

/**
 * Get current auth health status
 */
export function getAuthHealthStatus(): AuthHealthStatus {
  return _authHealthStatus;
}

/**
 * Check auth health and attempt recovery if needed
 * This is the main function that should be called before sync operations
 *
 * Returns:
 * - 'healthy': Auth is working fine
 * - 'recovered': Auth was broken but we fixed it by refreshing the session
 * - 'needs_login': Auth is broken and user needs to log in again
 */
export async function checkAndRecoverAuth(): Promise<AuthHealthStatus> {
  // Prevent concurrent recovery attempts
  if (_authRecoveryInProgress) {
    // Return current status without waiting - don't block sync
    return _authHealthStatus === 'checking' ? 'healthy' : _authHealthStatus;
  }

  // Skip if we checked recently and auth is healthy
  const now = Date.now();
  if (now - _lastAuthCheck < AUTH_CHECK_INTERVAL && _authHealthStatus === 'healthy') {
    return _authHealthStatus;
  }
  _lastAuthCheck = now;

  try {
    _authRecoveryInProgress = true;

    // Step 1: Quick localStorage check (synchronous, no hanging)
    const healthy = await isAuthHealthy();

    if (healthy) {
      // Don't log on every check - too noisy
      if (_authHealthStatus !== 'healthy') {
        console.log('[Supabase] Auth is healthy');
      }
      updateAuthHealthStatus('healthy');
      return 'healthy';
    }

    // Step 2: No valid session in localStorage
    const storageKey = 'sb-urkfvgqpzeuixgdayrex-auth-token';
    const storedSession = localStorage.getItem(storageKey);

    if (!storedSession) {
      console.log('[Supabase] No stored session, user needs to login');
      updateAuthHealthStatus('needs_login');
      return 'needs_login';
    }

    // Step 3: We have a stored session but it may be expired - try refresh
    // Only do this if we haven't tried recently (the interval check above handles this)
    console.log('[Supabase] Session may be expired, attempting refresh...');
    const refreshed = await refreshSession();

    if (refreshed) {
      console.log('[Supabase] Session refreshed successfully');
      updateAuthHealthStatus('recovered');
      return 'recovered';
    }

    // Refresh failed - user needs to login again
    console.log('[Supabase] Session refresh failed, user needs to login');
    updateAuthHealthStatus('needs_login');
    return 'needs_login';

  } finally {
    _authRecoveryInProgress = false;
  }
}

/**
 * Force check auth health now (ignores cooldown)
 */
export async function forceCheckAuthHealth(): Promise<AuthHealthStatus> {
  _lastAuthCheck = 0; // Reset cooldown
  return checkAndRecoverAuth();
}

// Expose auth utilities on window for debugging
if (typeof window !== 'undefined') {
  (window as any).isAuthHealthy = isAuthHealthy;
  (window as any).resetAuthState = resetAuthState;
  (window as any).refreshSession = refreshSession;
  (window as any).checkAndRecoverAuth = checkAndRecoverAuth;
  (window as any).forceCheckAuthHealth = forceCheckAuthHealth;
  (window as any).getAuthHealthStatus = getAuthHealthStatus;
}
